import React, { useMemo } from 'react';
import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

const STATUS_COLORS = {
  OK: '#2e7d32',
  Atenção: '#f9a825',
  Critico: '#d32f2f',
  Crítico: '#d32f2f',
  'N/A': '#9e9e9e',
  '(vazio)': '#bdbdbd',
};

const PALETTE = ['#1976d2', '#00897b', '#7b1fa2', '#ef6c00', '#c62828', '#455a64', '#5c6bc0'];

const paperSx = {
  p: 2,
  mt: 1.5,
  borderRadius: 2,
  border: '1px solid rgba(25,118,210,0.1)',
  background: 'linear-gradient(180deg, #fff 0%, #f8fafc 100%)',
};

function distToRows(distribution = {}) {
  return Object.entries(distribution).map(([name, value]) => ({
    name,
    value: Number(value) || 0,
  }));
}

function statusColor(name) {
  if (STATUS_COLORS[name]) return STATUS_COLORS[name];
  const n = String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (n === 'ok') return STATUS_COLORS.OK;
  if (n.includes('atenc')) return STATUS_COLORS.Atenção;
  if (n.includes('critic')) return STATUS_COLORS.Crítico;
  if (n === 'n/a' || n === 'na') return STATUS_COLORS['N/A'];
  return '#90a4ae';
}

/** Donut/pizza para status. */
export function StatusPieChart({ distribution, height = 220 }) {
  const data = useMemo(() => distToRows(distribution).filter((d) => d.value > 0), [distribution]);
  if (!data.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sem respostas de status.
      </Typography>
    );
  }
  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={48}
            outerRadius={78}
            paddingAngle={2}
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={statusColor(entry.name)} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => [v, 'Qtd']} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

/** Barras horizontais para seleção/checkbox. */
export function DistributionBarChart({ distribution, height = 220 }) {
  const data = useMemo(() => {
    return distToRows(distribution)
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [distribution]);

  if (!data.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sem distribuição.
      </Typography>
    );
  }

  const h = Math.max(height, 40 + data.length * 28);

  return (
    <Box sx={{ width: '100%', height: h }}>
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
          <Tooltip />
          <Bar dataKey="value" name="Qtd" radius={[0, 4, 4, 0]}>
            {data.map((entry, i) => (
              <Cell key={entry.name} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

/** Indicador visual para número (média/soma). */
export function NumberGauge({ numberAvg, numberSum, numberCount }) {
  if (!numberCount) {
    return (
      <Typography variant="caption" color="text.secondary">
        Sem valores numéricos.
      </Typography>
    );
  }
  const avg = numberAvg != null ? Number(numberAvg) : 0;
  const sum = numberSum != null ? Number(numberSum) : 0;
  const pct = Math.min(100, Math.max(0, (avg / (sum || avg || 1)) * 100));

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2">
        Média: <strong>{avg.toFixed(2)}</strong> · Soma: <strong>{sum}</strong> · N={numberCount}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Number.isFinite(pct) ? pct : 50}
        sx={{ mt: 1, height: 8, borderRadius: 4 }}
      />
    </Box>
  );
}

/** Bloco completo para uma pergunta do dashboard. */
export default function SafetyQuestionDistribution({ question }) {
  if (!question) return null;
  const type = question.responseType;

  return (
    <Paper elevation={0} sx={paperSx}>
      <Typography fontWeight={600}>
        {question.label}{' '}
        <Typography component="span" variant="caption" color="text.secondary">
          ({type}
          {question.isSubjectIdentifier ? ' · identificador' : ''}) — {question.totalAnswers} respostas
        </Typography>
      </Typography>
      {type === 'status' && <StatusPieChart distribution={question.distribution} />}
      {(type === 'selecao' || type === 'checkbox') && (
        <DistributionBarChart distribution={question.distribution} />
      )}
      {type === 'numero' && (
        <NumberGauge
          numberAvg={question.numberAvg}
          numberSum={question.numberSum}
          numberCount={question.numberCount}
        />
      )}
      {type === 'texto' && (
        <DistributionBarChart distribution={question.distribution} height={180} />
      )}
      {type === 'data' && (
        <DistributionBarChart distribution={question.distribution} height={180} />
      )}
      {!['status', 'selecao', 'checkbox', 'numero', 'texto', 'data'].includes(type) && (
        <DistributionBarChart distribution={question.distribution} />
      )}
    </Paper>
  );
}

const SEV_COLORS = {
  baixa: '#66bb6a',
  média: '#f9a825',
  media: '#f9a825',
  alta: '#e53935',
  '(vazio)': '#bdbdbd',
};

const ST_COLORS = {
  aberto: '#ef6c00',
  'em análise': '#1976d2',
  'em analise': '#1976d2',
  em_analise: '#1976d2',
  resolvido: '#2e7d32',
  '(vazio)': '#bdbdbd',
};

function pieFromMap(map, colorFn) {
  return Object.entries(map || {})
    .map(([name, value]) => ({ name, value: Number(value) || 0 }))
    .filter((d) => d.value > 0)
    .map((d) => ({ ...d, fill: colorFn(d.name) }));
}

export function NearMissDistributionCharts({ bySeverity = {}, byStatus = {}, title = 'Quase-falhas' }) {
  const sev = pieFromMap(bySeverity, (n) => {
    const k = String(n || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    return SEV_COLORS[k] || SEV_COLORS[n] || '#90a4ae';
  });
  const st = pieFromMap(byStatus, (n) => {
    const k = String(n || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    return ST_COLORS[k] || ST_COLORS[n] || '#90a4ae';
  });

  if (!sev.length && !st.length) {
    return (
      <Paper elevation={0} sx={{ ...paperSx, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Sem quase-falhas para gráfico.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {sev.length > 0 && (
        <Paper elevation={0} sx={{ ...paperSx, flex: '1 1 280px' }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>
            {title} · severidade
          </Typography>
          <Box sx={{ height: 200 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={sev} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {sev.map((e) => (
                    <Cell key={e.name} fill={e.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
      {st.length > 0 && (
        <Paper elevation={0} sx={{ ...paperSx, flex: '1 1 280px' }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>
            {title} · status
          </Typography>
          <Box sx={{ height: 200 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={st} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {st.map((e) => (
                    <Cell key={e.name} fill={e.fill} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

/** Comparativo por unidade (visão corporativa). */
export function UnitCompareBarChart({ byUnidade = [] }) {
  const data = useMemo(
    () =>
      (byUnidade || []).map((u) => ({
        name: u.nome || u.codigo,
        envios: u.envios || 0,
        quaseFalhas: u.quaseFalhas || 0,
        taxaResposta: u.taxaResposta || 0,
      })),
    [byUnidade]
  );

  if (!data.length) return null;

  return (
    <Paper elevation={0} sx={{ ...paperSx, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1 }}>
        Comparativo por unidade
      </Typography>
      <Box sx={{ width: '100%', height: Math.max(280, 60 + data.length * 40) }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11 }}
              interval={0}
              angle={data.length > 4 ? -25 : 0}
              textAnchor={data.length > 4 ? 'end' : 'middle'}
              height={data.length > 4 ? 70 : 30}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend
              formatter={(v) =>
                v === 'envios' ? 'Envios' : v === 'quaseFalhas' ? 'Quase-falhas' : 'Taxa resposta %'
              }
            />
            <Bar dataKey="envios" fill="#1976d2" radius={[4, 4, 0, 0]} />
            <Bar dataKey="quaseFalhas" fill="#00897b" radius={[4, 4, 0, 0]} />
            <Bar dataKey="taxaResposta" fill="#7b1fa2" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

/** Comparativo por setor: formulários, envios, quase-falhas. */
export function SectorCompareBarChart({ bySector = [] }) {
  const data = useMemo(
    () =>
      (bySector || []).map((s) => ({
        name: s.name,
        formularios: s.forms || 0,
        envios: s.envios || 0,
        quaseFalhas: s.quaseFalhas || 0,
      })),
    [bySector]
  );

  if (!data.length) return null;

  return (
    <Paper elevation={0} sx={{ ...paperSx, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 1 }}>
        Comparativo por setor
      </Typography>
      <Box sx={{ width: '100%', height: Math.max(260, 60 + data.length * 36) }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={data.length > 4 ? -20 : 0} textAnchor={data.length > 4 ? 'end' : 'middle'} height={data.length > 4 ? 60 : 30} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend
              formatter={(v) =>
                v === 'formularios' ? 'Formulários' : v === 'envios' ? 'Envios' : 'Quase-falhas'
              }
            />
            <Bar dataKey="formularios" fill="#7b1fa2" radius={[4, 4, 0, 0]} />
            <Bar dataKey="envios" fill="#1976d2" radius={[4, 4, 0, 0]} />
            <Bar dataKey="quaseFalhas" fill="#00897b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
